import dayjs from 'dayjs';
import type { CargoHistoryEntry } from '../types';

export type CargoHistoryRowWithName = CargoHistoryEntry & { cargoName: string };

/** Явный false с API — строка не отражает текущее распределение. */
export function cargoHistoryRowIsStale(entry: CargoHistoryEntry): boolean {
  return entry.isActual === false;
}

/** Заголовок события в таблице (для ROUTE_DELETED — фиксированная формулировка без акцента на номере). */
export function cargoHistoryEventDisplayText(entry: CargoHistoryEntry): string {
  if (entry.action === 'ROUTE_DELETED') return 'Маршрут удалён, груз снят';
  if (entry.action === 'ROUTE_STARTED') return 'Отправление в путь (ROUTE_STARTED)';
  if (entry.action === 'ROUTE_COMPLETED') return 'Маршрут завершён (ROUTE_COMPLETED)';
  return cargoHistoryEventLabel(entry);
}

/** Описание с пометкой неактуальности по правилам UX. */
export function cargoHistoryDescriptionDisplayText(entry: CargoHistoryEntry): string {
  const raw = entry.description && String(entry.description).trim();
  const base = raw || '—';
  if (entry.isActual !== false) return base;
  if (entry.action === 'ROUTE_DELETED') {
    return base === '—' ? '(маршрут удалён)' : `${base} (маршрут удалён)`;
  }
  return base === '—' ? '(устарело)' : `${base} (устарело)`;
}

/** Короткая подпись события для сводной таблицы (actionType + контекст из описания). */
export function cargoHistoryEventLabel(entry: CargoHistoryEntry): string {
  const raw = entry.action;
  if (raw === 'ALLOCATED') return 'Назначен в маршрут';
  if (raw === 'UNALLOCATED') return 'Снят с маршрута';
  if (raw === 'ROUTE_DELETED') return 'Маршрут удалён, груз снят';
  if (raw === 'ROUTE_STARTED') return 'Маршрут в пути';
  if (raw === 'ROUTE_COMPLETED') return 'Маршрут завершён';
  const d = (entry.description || '').toLowerCase();
  switch (entry.actionType) {
    case 'creation':
      return 'Создание';
    case 'deletion':
      return 'Удаление';
    case 'status_change':
      return 'Изменение статуса';
    case 'update':
      return 'Изменение';
    case 'location_change':
      if (d.includes('склад') || d.includes('принят')) return 'Склад / местоположение';
      if (d.includes('маршрут') || d.includes('назначен')) return 'Назначен в маршрут';
      return 'Местоположение';
    default:
      return String(entry.actionType);
  }
}

export function historyEntrySortTimestamp(entry: CargoHistoryEntry): number {
  return dayjs(entry.eventDate || entry.createdAt).valueOf();
}

export function mergeCargoHistories(
  items: { cargoId: number; cargoName: string; entries: CargoHistoryEntry[] }[]
): CargoHistoryRowWithName[] {
  const rows: CargoHistoryRowWithName[] = [];
  for (const bucket of items) {
    for (const e of bucket.entries) {
      rows.push({ ...e, cargoName: bucket.cargoName });
    }
  }
  rows.sort((a, b) => {
    const tb = historyEntrySortTimestamp(b);
    const ta = historyEntrySortTimestamp(a);
    if (tb !== ta) return tb - ta;
    return (b.id ?? 0) - (a.id ?? 0);
  });
  return rows;
}
